// =============================================================================
// PPIInclusao — incluir na prova quem apareceu no dia e não estava na lista
// =============================================================================
// Nasceu em 2026-08-24. No dia da aplicação aparecem pessoas fora da lista porque
// algum gerente pediu. Antes disto não havia caminho pela tela: candidato só
// nascia da inscrição pública, e a prova só abre para quem a triagem aprovou.
//
// Escrito UMA vez, num arquivo só, mesmo servindo hoje a uma tela: é a lição de
// 05/08 (dois formulários do Treinamento que eram cópia um do outro custaram
// meses com uma trava de perfil faltando num dos lados) e o padrão que a
// desistência já segue.
//
// ⚠️ O motivo é OBRIGATÓRIO aqui e no servidor. Sem o porquê, daqui a três meses
// ninguém sabe se a pessoa entrou por pedido da GN, por correção de cadastro ou
// por favor — e as três levam a decisões diferentes.
//
// ⚠️ Incluir NÃO marca presença. A presença é o porteiro da prova e é ato do
// aplicador na sala, olhando a pessoa.

import React, { useState, useEffect, useRef } from 'react';
import { UserPlus, X, AlertTriangle, Search } from 'lucide-react';
import { CORES, BadgeStatus, Botao, estilos } from './ppiUI';

const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
});

/** A marca que acompanha o incluído em toda lista. Uma só, para não divergirem. */
export function MarcaIncluido({ inclusao }) {
    if (!inclusao) return null;
    // ⚠️ Reconvocar quem a triagem REPROVOU é um ato mais forte que incluir quem
    // nunca se inscreveu — a marca diz qual dos dois foi, porque a diferença
    // muda o que quem lê deve conferir.
    const reprovado = inclusao.triagemAnterior === 'Reprovado';
    const titulo = [
        reprovado
            ? 'Estava REPROVADO(A) na triagem e foi reconvocado(a)'
            : 'Incluído(a) na prova sem passar pela triagem',
        inclusao.motivo ? `Motivo: ${inclusao.motivo}` : null,
        inclusao.por ? `Por: ${inclusao.por}${inclusao.em ? ` em ${inclusao.em}` : ''}` : null,
        inclusao.foraDoRecorte ? 'Estava FORA do recorte do processo' : null,
    ].filter(Boolean).join('\n');

    return (
        <span title={titulo} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <BadgeStatus tom={reprovado ? 'reprovado' : 'alerta'}>
                {reprovado ? 'incluído (era reprovado)' : 'incluído depois'}
            </BadgeStatus>
        </span>
    );
}

/**
 * O modal. `idVaga` é a vaga selecionada na tela; `onIncluido` recebe a mensagem
 * do servidor (que diz o que de fato aconteceu — criar, reconvocar, outra vaga).
 */
export function ModalIncluirCandidato({ idVaga, cargoVaga, onFechar, onIncluido }) {
    const [termo, setTermo] = useState('');
    const [resultados, setResultados] = useState([]);
    const [buscando, setBuscando] = useState(false);
    const [escolhido, setEscolhido] = useState(null);
    const [motivo, setMotivo] = useState('');
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState('');
    // Cada busca carrega o seu número; só a MAIS RECENTE pode escrever na tela.
    // Sem isso, uma resposta lenta de "MA" chegaria depois de "MARIA" e repovoaria
    // a lista com o resultado errado — a pessoa clicaria num nome que não buscou.
    const seq = useRef(0);

    useEffect(() => {
        const t = String(termo).trim();
        if (t.length < 3) { setResultados([]); return undefined; }
        const meu = ++seq.current;
        const timer = setTimeout(async () => {
            setBuscando(true); setErro('');
            try {
                const r = await fetch(
                    `/api/treinamento/ppi/vagas/${idVaga}/candidatos-para-incluir?termo=${encodeURIComponent(t)}`,
                    { headers: authHeaders() });
                const d = await r.json();
                if (meu !== seq.current) return;   // chegou fora de ordem: descarta
                if (!r.ok) throw new Error(d.message || 'Erro ao buscar.');
                setResultados(Array.isArray(d) ? d : []);
            } catch (e) {
                if (meu === seq.current) { setErro(e.message); setResultados([]); }
            } finally {
                if (meu === seq.current) setBuscando(false);
            }
        }, 350);   // freio: não dispara uma busca por tecla
        return () => clearTimeout(timer);
    }, [termo, idVaga]);

    const incluir = async () => {
        setSalvando(true); setErro('');
        try {
            const r = await fetch(`/api/treinamento/ppi/vagas/${idVaga}/incluir-candidato`, {
                method: 'POST', headers: authHeaders(),
                body: JSON.stringify({ idFuncionario: escolhido.idFuncionario, motivo }),
            });
            const d = await r.json();
            if (!r.ok) throw new Error(d.message || 'Erro ao incluir.');
            onIncluido(d.message);
            onFechar();
        } catch (e) { setErro(e.message); }
        finally { setSalvando(false); }
    };

    // O que já existe daquela pessoa no processo — muda o que o botão vai fazer.
    const situacao = (p) => {
        if (!p.jaInscrito) return null;
        if (p.jaInscrito.respondeu) return { tom: 'neutro', texto: 'já entregou a prova', bloqueia: true };
        if (p.jaInscrito.status === 'Desistente') return { tom: 'neutro', texto: 'desistiu do processo', bloqueia: true };
        if (p.jaInscrito.incluido) return { tom: 'alerta', texto: 'já foi incluída', bloqueia: true };
        if (p.jaInscrito.triagem === 'Aprovado') return { tom: 'aprovado', texto: 'já convocada pela triagem', bloqueia: true };
        if (p.jaInscrito.triagem === 'Reprovado') return { tom: 'reprovado', texto: `reprovada na triagem · ${p.jaInscrito.cargoVaga}`, bloqueia: false };
        return { tom: 'rascunho', texto: `inscrita, ainda não triada · ${p.jaInscrito.cargoVaga}`, bloqueia: false };
    };

    return (
        <div style={overlay} onClick={(e) => { if (e.target === e.currentTarget) onFechar(); }}>
            <div style={painel}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                    <UserPlus size={20} color={CORES.primaria} />
                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: CORES.texto }}>
                        Incluir na prova — {cargoVaga}
                    </h3>
                    <button onClick={onFechar} style={btnFechar} title="Fechar"><X size={20} /></button>
                </div>
                <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: CORES.textoSec, lineHeight: 1.5 }}>
                    Para quem apareceu no dia e não está na lista. A pessoa é convocada <strong>sem passar
                    pela triagem</strong> e fica marcada como <strong>incluída depois</strong> em todas as listas
                    e no PDF. Depois de incluir, <strong>marque a presença</strong> — é ela que abre a prova.
                </p>

                {!escolhido ? (
                    <>
                        <label style={estilos.rotuloFiltro}>Buscar funcionário (nome ou CPF)</label>
                        <div style={{ position: 'relative' }}>
                            <Search size={16} color={CORES.textoFraco}
                                style={{ position: 'absolute', left: '11px', top: '50%', transform: 'translateY(-50%)' }} />
                            <input autoFocus value={termo} onChange={(e) => setTermo(e.target.value)}
                                placeholder="Digite ao menos 3 letras…"
                                style={{ ...estilos.input, paddingLeft: '34px' }} />
                        </div>

                        {erro && <p style={{ color: '#be123c', fontSize: '13px' }}>{erro}</p>}
                        {buscando && <p style={{ color: CORES.textoFraco, fontSize: '13px' }}>Buscando…</p>}
                        {!buscando && termo.trim().length >= 3 && !resultados.length && !erro && (
                            <p style={{ color: CORES.textoFraco, fontSize: '13px' }}>
                                Ninguém encontrado entre os funcionários ativos.
                            </p>
                        )}

                        <div style={{ maxHeight: '320px', overflowY: 'auto', marginTop: '10px' }}>
                            {resultados.map((p) => {
                                const s = situacao(p);
                                return (
                                    <div key={p.idFuncionario} style={linhaPessoa}>
                                        <div style={{ minWidth: 0 }}>
                                            <strong style={{ fontSize: '14px', color: CORES.texto }}>{p.nome}</strong>
                                            <div style={{ fontSize: '12px', color: CORES.textoFraco }}>
                                                {p.loja} · {p.cargo}
                                            </div>
                                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '4px' }}>
                                                {s && <BadgeStatus tom={s.tom}>{s.texto}</BadgeStatus>}
                                                {/* Fora do recorte NÃO bloqueia (decisão da Julia): o pedido
                                                    do gerente é real, e travar empurraria o caso para um
                                                    UPDATE na mão. Mas fica visível e é GRAVADO. */}
                                                {p.foraDoRecorte && (
                                                    <BadgeStatus tom="alerta">fora do recorte</BadgeStatus>
                                                )}
                                            </div>
                                        </div>
                                        <Botao variant={s?.bloqueia ? 'secundario' : 'primario'}
                                            onClick={() => { setEscolhido(p); setErro(''); }}
                                            disabled={Boolean(s?.bloqueia)}
                                            title={s?.bloqueia ? `Não é possível incluir: ${s.texto}` : 'Incluir esta pessoa'}
                                            style={{ padding: '6px 12px', fontSize: '12px', flexShrink: 0 }}>
                                            {s && !s.bloqueia ? 'Reconvocar' : 'Incluir'}
                                        </Botao>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                ) : (
                    <>
                        <div style={caixaEscolhido}>
                            <div>
                                <strong style={{ fontSize: '15px', color: CORES.texto }}>{escolhido.nome}</strong>
                                <div style={{ fontSize: '12px', color: CORES.textoFraco }}>
                                    {escolhido.loja} · {escolhido.cargo}
                                </div>
                            </div>
                            <Botao variant="secundario" onClick={() => { setEscolhido(null); setMotivo(''); }}
                                style={{ padding: '5px 10px', fontSize: '12px' }}>Trocar</Botao>
                        </div>

                        {/* Os dois avisos que mudam o peso do ato — ditos ANTES do clique. */}
                        {escolhido.foraDoRecorte && (
                            <div style={aviso}>
                                <AlertTriangle size={16} color="#b45309" style={{ flexShrink: 0, marginTop: '1px' }} />
                                <div>
                                    <strong>Fora do recorte deste processo.</strong> Esta pessoa não entraria pelo
                                    filtro de loja/cargo/centro de lucro que o processo define. A inclusão é permitida
                                    e fica <strong>registrada com essa marca</strong>.
                                </div>
                            </div>
                        )}
                        {escolhido.jaInscrito?.triagem === 'Reprovado' && (
                            <div style={aviso}>
                                <AlertTriangle size={16} color="#b45309" style={{ flexShrink: 0, marginTop: '1px' }} />
                                <div>
                                    <strong>Esta pessoa foi REPROVADA na triagem.</strong> Incluí-la a reconvoca para a
                                    prova. A reprovação <strong>não é apagada</strong> — fica registrada, e o histórico
                                    passa a mostrar “Incluído após reprovação na triagem”.
                                </div>
                            </div>
                        )}

                        <label style={{ ...estilos.rotuloFiltro, marginTop: '14px' }}>
                            Por que esta pessoa está sendo incluída? (obrigatório)
                        </label>
                        <textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={3}
                            placeholder="Ex.: pedido da GN da SVV — a funcionária não recebeu o link a tempo."
                            style={{ ...estilos.input, resize: 'vertical', fontFamily: 'inherit' }} />
                        <p style={{ fontSize: '12px', color: CORES.textoFraco, margin: '6px 0 0 0' }}>
                            Escreva <strong>quem pediu</strong> — é o que explica a inclusão daqui a três meses.
                        </p>

                        {erro && <p style={{ color: '#be123c', fontSize: '13px' }}>{erro}</p>}

                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
                            <Botao variant="secundario" onClick={onFechar}>Cancelar</Botao>
                            <Botao icone={UserPlus} onClick={incluir} disabled={!motivo.trim() || salvando}>
                                {salvando ? 'Incluindo…' : 'Incluir na prova'}
                            </Botao>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

const overlay = {
    position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', zIndex: 1000,
};
const painel = {
    backgroundColor: '#fff', borderRadius: '14px', padding: '22px',
    width: '100%', maxWidth: '600px', maxHeight: '88vh', overflowY: 'auto',
    boxShadow: '0 20px 50px -12px rgba(0,0,0,0.3)',
};
const btnFechar = {
    marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer',
    color: CORES.textoFraco, padding: '4px', display: 'flex',
};
const linhaPessoa = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
    padding: '10px 0', borderBottom: `1px solid ${CORES.borda}`,
};
const caixaEscolhido = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
    backgroundColor: '#f8fafc', border: `1px solid ${CORES.borda}`,
    borderRadius: '10px', padding: '12px 14px',
};
const aviso = {
    display: 'flex', gap: '10px', alignItems: 'flex-start', marginTop: '12px',
    backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '10px',
    padding: '12px 14px', fontSize: '13px', color: '#92400e', lineHeight: 1.5,
};
